import { useCallback, useEffect, useRef } from 'react';

/**
 * Plays a "new order" notification chime using the Web Audio API.
 *
 * Primary path: synthesize a two-tone bell chime entirely in-browser —
 * no external file dependency, works as long as AudioContext is unlocked.
 *
 * Fallback path: stream /notification.wav via HTMLAudioElement for browsers
 * that block AudioContext creation (e.g. very old Safari).
 *
 * Autoplay unlock: on the first user gesture (click / keydown / touchstart)
 * we create and immediately resume the AudioContext so subsequent calls to
 * playSound() never hit the "suspended" gate.
 */
export function useNewOrderSound() {
  const audioCtxRef = useRef<AudioContext | null>(null);
  const audioElRef = useRef<HTMLAudioElement | null>(null);

  const getContext = useCallback((): AudioContext | null => {
    if (typeof AudioContext === 'undefined' && typeof (window as any).webkitAudioContext === 'undefined') {
      return null;
    }
    if (!audioCtxRef.current) {
      const Ctx: typeof AudioContext = window.AudioContext ?? (window as any).webkitAudioContext;
      audioCtxRef.current = new Ctx();
    }
    return audioCtxRef.current;
  }, []);

  const resumeContext = useCallback(async (ctx: AudioContext) => {
    if (ctx.state === 'suspended') {
      await ctx.resume().catch(() => {});
    }
  }, []);

  /**
   * Synthesizes a short two-tone ascending bell chime.
   * Using OscillatorNode + GainNode avoids any file I/O.
   */
  const synthesizeChime = useCallback((ctx: AudioContext) => {
    const now = ctx.currentTime;

    const playTone = (frequency: number, startAt: number, duration: number, peakGain: number) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(frequency, startAt);

      gain.gain.setValueAtTime(0, startAt);
      gain.gain.linearRampToValueAtTime(peakGain, startAt + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, startAt + duration);

      // Add a second harmonic for a richer bell timbre
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(frequency * 2.756, startAt);
      gain2.gain.setValueAtTime(0, startAt);
      gain2.gain.linearRampToValueAtTime(peakGain * 0.35, startAt + 0.01);
      gain2.gain.exponentialRampToValueAtTime(0.001, startAt + duration * 0.7);

      osc.connect(gain);
      osc2.connect(gain2);
      gain.connect(ctx.destination);
      gain2.connect(ctx.destination);

      osc.start(startAt);
      osc2.start(startAt);
      osc.stop(startAt + duration);
      osc2.stop(startAt + duration * 0.7);
    };

    playTone(1046.5, now, 0.55, 0.55);       // C6 — first ding
    playTone(1318.5, now + 0.13, 0.65, 0.45); // E6 — second ding, slightly softer
  }, []);

  const unlockAudio = useCallback(async () => {
    const ctx = getContext();
    if (!ctx) return;
    await resumeContext(ctx);
  }, [getContext, resumeContext]);

  useEffect(() => {
    const onGesture = () => { void unlockAudio(); };
    document.addEventListener('click', onGesture, { once: true });
    document.addEventListener('keydown', onGesture, { once: true });
    document.addEventListener('touchstart', onGesture, { once: true, passive: true });
    return () => {
      document.removeEventListener('click', onGesture);
      document.removeEventListener('keydown', onGesture);
      document.removeEventListener('touchstart', onGesture);
      audioCtxRef.current?.close().catch(() => {});
      audioCtxRef.current = null;
    };
  }, [unlockAudio]);

  const playSound = useCallback(async () => {
    const ctx = getContext();

    if (ctx && ctx.state !== 'closed') {
      try {
        await resumeContext(ctx);
        synthesizeChime(ctx);
        return;
      } catch {
        // fall through
      }
    }

    // HTMLAudioElement fallback
    try {
      if (!audioElRef.current) {
        audioElRef.current = new Audio('/notification.wav');
        audioElRef.current.preload = 'auto';
      } else {
        audioElRef.current.currentTime = 0;
      }
      await audioElRef.current.play();
    } catch {
      // Autoplay blocked — user hasn't interacted yet.
    }
  }, [getContext, resumeContext, synthesizeChime]);

  return { playSound };
}
